/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Source: https://github.com/backstage/backstage/blob/master/plugins/catalog/src/components/EntityContextMenu/EntityContextMenu.tsx */

import type { IconProps } from '@mui/material';
import type { ComponentType } from 'react';

import { toastApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { Icon } from '@iconify/react';
import { CircleButton } from '@ipen-portal/plugin-common-react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useCopyToClipboard } from 'react-use';

import type { UnregisterEntityOptions } from './UnregisterEntity';

import { UnregisterEntity } from './UnregisterEntity';

/** @public */
export type EntityContextMenuClassKey = 'button';

type EntityContextMenuProps = {
  onInspectEntity: () => void;
  onUnregisterEntity: () => void;
  UNSTABLE_contextMenuOptions?: UnregisterEntityOptions;
  UNSTABLE_extraContextMenuItems?: ExtraContextMenuItem[];
};

type ExtraContextMenuItem = {
  Icon: ComponentType<IconProps>;
  onClick: () => void;
  title: string;
};

export const EntityContextMenu = (props: EntityContextMenuProps) => {
  const {
    onInspectEntity,
    onUnregisterEntity,
    UNSTABLE_contextMenuOptions,
    UNSTABLE_extraContextMenuItems,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const unregisterPermission = useEntityPermission(
    catalogEntityDeletePermission,
  );
  const isAllowed = unregisterPermission.allowed;

  const onOpen = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const toastApi = useApi(toastApiRef);
  const [copyState, copyToClipboard] = useCopyToClipboard();
  useEffect(() => {
    if (!copyState.error && copyState.value) {
      toastApi.post({
        status: 'info',
        timeout: 5000,
        title: 'Copied!',
      });
    }
  }, [copyState, toastApi]);

  const extraItems = UNSTABLE_extraContextMenuItems && [
    ...UNSTABLE_extraContextMenuItems.map(item => (
      <MenuItem
        key={item.title}
        onClick={() => {
          onClose();
          item.onClick();
        }}
      >
        <ListItemIcon>
          <item.Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={item.title} />
      </MenuItem>
    )),
    <Divider key="divider" />,
  ];

  return (
    <>
      <Tooltip arrow title="More">
        <CircleButton
          aria-controls="long-menu"
          aria-expanded={!!anchorEl}
          aria-haspopup="true"
          aria-label="more"
          data-testid="menu-button"
          id="long-menu"
          onClick={onOpen}
          role="button"
        >
          <Icon height={20} icon="material-symbols:more-vert" />
        </CircleButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        aria-labelledby="long-menu"
        onClose={onClose}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuList autoFocusItem={Boolean(anchorEl)}>
          {extraItems}
          <UnregisterEntity
            isUnregisterAllowed={isAllowed}
            onClose={onClose}
            onUnregisterEntity={onUnregisterEntity}
            unregisterEntityOptions={UNSTABLE_contextMenuOptions}
          />
          <MenuItem
            onClick={() => {
              onClose();
              onInspectEntity();
            }}
          >
            <ListItemIcon>
              <Icon height={20} icon="material-symbols:bug-report" />
            </ListItemIcon>
            <ListItemText primary="Inspect entity" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              onClose();
              copyToClipboard(globalThis.location.toString());
            }}
          >
            <ListItemIcon>
              <Icon height={20} icon="material-symbols:file-copy" />
            </ListItemIcon>
            <ListItemText primary="Copy entity URL" />
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
};
